import React, { useState } from 'react';
import { Upload, Trash2, ExternalLink, Image as ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

const PortfolioManagement = ({ apiKey }) => {
  const [images, setImages] = useState([
    { id: 1, url: 'https://images.unsplash.com/photo-1519741497674-611481863552', alt: 'Mariage 1' },
    { id: 2, url: 'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6', alt: 'Mariage 2' },
    { id: 3, url: 'https://images.unsplash.com/photo-1606800052052-a08af7148866', alt: 'Mariage 3' },
    { id: 4, url: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc', alt: 'Événement 1' },
    { id: 5, url: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3', alt: 'Événement 2' },
    { id: 6, url: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed', alt: 'Événement 3' },
    { id: 7, url: 'https://images.unsplash.com/photo-1520854221256-17451cc331bf', alt: 'Événement 4' },
    { id: 8, url: 'https://images.unsplash.com/photo-1522413452208-996ff3f3e740', alt: 'Événement 5' }
  ]);
  
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState(null);

  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner une image valide' });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'La taille de l\'image ne doit pas dépasser 10MB' });
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setMessage(null);

    try {
      // Step 1: Get signature from backend
      setUploadProgress(20);
      const sigResponse = await fetch(`${apiUrl}/api/cloudinary/signature?resource_type=image&folder=portfolio`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });

      if (!sigResponse.ok) {
        throw new Error('Erreur lors de la génération de la signature');
      }

      const sig = await sigResponse.json();
      
      // Step 2: Upload to Cloudinary
      setUploadProgress(40);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);

      const uploadResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );

      if (!uploadResponse.ok) {
        throw new Error('Erreur lors de l\'upload vers Cloudinary');
      }

      const result = await uploadResponse.json();
      setUploadProgress(100);

      // Add to images list
      const newImage = {
        id: Date.now(),
        url: result.secure_url,
        alt: file.name,
        public_id: result.public_id
      };

      setImages([...images, newImage]);
      setMessage({ type: 'success', text: 'Image uploadée avec succès !' });

      // Reset file input
      e.target.value = '';
      
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({ type: 'error', text: `Erreur: ${error.message}` });
    } finally {
      setUploading(false);
      setTimeout(() => {
        setUploadProgress(0);
        setMessage(null);
      }, 3000);
    }
  };

  const handleDeleteImage = async (imageId, publicId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
      return;
    }

    try {
      // If image has public_id, delete from Cloudinary
      if (publicId) {
        const response = await fetch(`${apiUrl}/api/cloudinary/delete?public_id=${encodeURIComponent(publicId)}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${apiKey}` }
        });

        if (!response.ok) {
          throw new Error('Erreur lors de la suppression sur Cloudinary');
        }
      }

      // Remove from list
      setImages(images.filter(img => img.id !== imageId));
      setMessage({ type: 'success', text: 'Image supprimée avec succès' });
      setTimeout(() => setMessage(null), 3000);

    } catch (error) {
      console.error('Delete error:', error);
      setMessage({ type: 'error', text: `Erreur: ${error.message}` });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleAddByUrl = () => {
    const url = prompt('Entrez l\'URL de l\'image:');
    if (url && url.trim()) {
      const newImage = {
        id: Date.now(),
        url: url.trim(),
        alt: 'Image portfolio'
      };
      setImages([...images, newImage]);
      setMessage({ type: 'success', text: 'Image ajoutée avec succès !' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Gestion du Portfolio</h2>
        <p className="text-gray-600 mt-1">{images.length} image(s) dans le portfolio</p>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      {/* Upload Section */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">📤 Ajouter des Images</h3>
          
          <div className="space-y-4">
            {/* File Upload */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-amber-500 transition-colors">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className={`cursor-pointer flex flex-col items-center ${uploading ? 'opacity-50' : ''}`}
              >
                <Upload className="h-12 w-12 text-gray-400 mb-4" />
                <p className="text-lg font-semibold text-gray-900 mb-2">
                  {uploading ? 'Upload en cours...' : 'Cliquez pour uploader une image'}
                </p>
                <p className="text-sm text-gray-600">
                  PNG, JPG, WEBP jusqu'à 10MB
                </p>
              </label>
            </div>

            {/* Progress Bar */}
            {uploading && (
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className="bg-gradient-to-r from-amber-500 to-amber-600 h-4 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            )}

            {/* URL Input */}
            <div className="flex items-center gap-2">
              <span className="text-gray-600 text-sm">ou</span>
              <Button
                onClick={handleAddByUrl}
                disabled={uploading}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Ajouter par URL
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Images Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {images.map((image) => (
          <Card key={image.id} className="overflow-hidden hover:shadow-xl transition-shadow">
            <CardContent className="p-0">
              <div className="relative group">
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-64 object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400x300?text=Image+Error';
                  }}
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center">
                  <Button
                    onClick={() => handleDeleteImage(image.id, image.public_id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity bg-red-500 hover:bg-red-600 text-white"
                  >
                    <Trash2 className="h-5 w-5 mr-2" />
                    Supprimer
                  </Button>
                </div>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-600 truncate">{image.alt}</p>
                {image.public_id && (
                  <p className="text-xs text-gray-400 mt-1">☁️ Cloudinary</p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {images.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <ImageIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">Aucune image dans le portfolio</p>
            <p className="text-sm text-gray-500 mt-2">Uploadez votre première image pour commencer</p>
          </CardContent>
        </Card>
      )}

      {/* Info */}
      <Card className="mt-6 bg-blue-50 border-blue-200">
        <CardContent className="p-6">
          <h3 className="font-bold text-blue-900 mb-2">☁️ Upload Cloudinary Actif</h3>
          <p className="text-sm text-blue-800">
            Les images sont maintenant uploadées directement sur Cloudinary avec CDN rapide et optimisation automatique. 
            Les anciennes images par URL restent disponibles.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PortfolioManagement;
